import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output } from "@angular/core";

/**
 * Emits `seen` once the host has genuinely been read on screen: in view "enough" for
 * `dwellMs` without a break. Used by the detailed community feed, where every post is
 * rendered in full and there is no "open" to mark it read — scrolling it into view is the
 * reading.
 *
 * "Enough" is half the post visible, OR the post filling half the viewport — a post taller
 * than the screen can never be 50% visible, and without the second condition a long post
 * could never be marked. The dwell stops a fast scroll past from counting.
 *
 * Only observes while `seenEnabled` is true (the feed passes "this post is unread"), and
 * fires at most once per enable, so a read post costs nothing.
 */
@Directive({
  selector: "[mypSeenInView]",
  standalone: false,
})
export class SeenInViewDirective implements OnDestroy {
  @Input() set seenEnabled(value: boolean) {
    const next = !!value;
    if (next === this._enabled) return;
    this._enabled = next;
    if (next) this._observe();
    else this._stop();
  }
  @Input() dwellMs = 1000;
  @Output() seen = new EventEmitter<void>();

  private _enabled = false;
  private _observer: IntersectionObserver | null = null;
  private _timer: any = null;

  constructor(
    private _el: ElementRef<HTMLElement>,
    private _zone: NgZone
  ) {}

  ngOnDestroy() {
    this._stop();
  }

  private _observe() {
    if (typeof IntersectionObserver === "undefined" || this._observer) return;
    // Outside Angular: intersection callbacks fire on every scroll step, and none of them
    // need change detection until `seen` actually emits.
    this._zone.runOutsideAngular(() => {
      this._observer = new IntersectionObserver((entries) => entries.forEach((entry) => this._onEntry(entry)), {
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      this._observer.observe(this._el.nativeElement);
    });
  }

  private _onEntry(entry: IntersectionObserverEntry) {
    const viewport = entry.rootBounds?.height || window.innerHeight || 0;
    const enough = entry.isIntersecting && (entry.intersectionRatio >= 0.5 || (viewport > 0 && entry.intersectionRect.height >= viewport * 0.5));
    if (!enough) {
      clearTimeout(this._timer);
      this._timer = null;
      return;
    }
    if (this._timer) return;
    this._timer = setTimeout(() => {
      this._timer = null;
      if (!this._enabled) return;
      this._stop();
      this._zone.run(() => this.seen.emit());
    }, this.dwellMs);
  }

  private _stop() {
    clearTimeout(this._timer);
    this._timer = null;
    this._observer?.disconnect();
    this._observer = null;
  }
}
